import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Modal from "../Modal";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Field, FieldLabel, FieldMessage } from "../ui/field";
import { formatApiError } from "../../lib/api";
import { createFirmware, updateFirmware } from "../../lib/firmwareApi";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard";

export const EMPTY_FIRMWARE_FORM = Object.freeze({
  model: "",
  firmware_version: "",
  firmware_type: "",
  android_version: "",
  firmware_url: "",
  apdb_url: "",
  status: "active",
});

const REQUIRED_FIELDS = [
  "model",
  "firmware_version",
  "firmware_type",
  "android_version",
  "firmware_url",
  "status",
];

const FIELD_LABELS = {
  model: "Model",
  firmware_version: "Firmware Version",
  firmware_type: "Firmware Type",
  android_version: "Android Version",
  firmware_url: "Firmware URL",
  apdb_url: "APDB URL",
  status: "Status",
};

function isHttpUrl(value) {
  try {
    const url = new URL(value);
    return (url.protocol === "http:" || url.protocol === "https:") && Boolean(url.hostname);
  } catch {
    return false;
  }
}

export function buildFirmwarePayload(values) {
  return {
    model: String(values.model || "").trim(),
    firmware_version: String(values.firmware_version || "").trim(),
    firmware_type: String(values.firmware_type || "").trim(),
    android_version: String(values.android_version || "").trim(),
    firmware_url: String(values.firmware_url || "").trim(),
    apdb_url: String(values.apdb_url || "").trim() || null,
    status: String(values.status || "").trim(),
  };
}

export function validateFirmwarePayload(payload) {
  const errors = {};
  REQUIRED_FIELDS.forEach((field) => {
    if (!payload[field]) errors[field] = `${FIELD_LABELS[field]} is required.`;
  });
  if (payload.firmware_url && !isHttpUrl(payload.firmware_url)) {
    errors.firmware_url = "Firmware URL must be an absolute HTTP or HTTPS URL.";
  }
  if (payload.apdb_url && !isHttpUrl(payload.apdb_url)) {
    errors.apdb_url = "APDB URL must be an absolute HTTP or HTTPS URL.";
  }
  if (payload.status && !["active", "inactive"].includes(payload.status)) {
    errors.status = "Status must be Active or Inactive.";
  }
  return errors;
}

function valuesFromRecord(record) {
  if (!record) return { ...EMPTY_FIRMWARE_FORM };
  return {
    model: record.model || "",
    firmware_version: record.firmware_version || "",
    firmware_type: record.firmware_type || "",
    android_version: record.android_version || "",
    firmware_url: record.firmware_url || "",
    apdb_url: record.apdb_url || "",
    status: record.status || "active",
  };
}

function changedPayload(record, payload) {
  if (!record) return payload;
  const original = buildFirmwarePayload(valuesFromRecord(record));
  return Object.fromEntries(
    Object.entries(payload).filter(([field, value]) => original[field] !== value)
  );
}

function apiFieldErrors(error) {
  const detail = error?.response?.data?.detail;
  if (!Array.isArray(detail)) return {};
  return Object.fromEntries(
    detail.flatMap((entry) => {
      const field = entry?.loc?.[entry.loc.length - 1];
      return FIELD_LABELS[field] && entry?.msg ? [[field, entry.msg]] : [];
    })
  );
}

function FormField({ id, label, error, optional = false, children }) {
  return (
    <Field>
      <FieldLabel htmlFor={`firmware-${id}`} required={!optional}>{label}</FieldLabel>
      {children}
      <FieldMessage id={`firmware-${id}-error`} data-testid={`firmware-error-${id}`}>{error}</FieldMessage>
    </Field>
  );
}

export default function FirmwareFormDialog({ open, record, onClose, onSaved }) {
  const [values, setValues] = useState(EMPTY_FIRMWARE_FORM);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const baselineRef = useRef(buildFirmwarePayload(EMPTY_FIRMWARE_FORM));
  const isEdit = Boolean(record);

  useEffect(() => {
    if (!open) return;
    setValues(valuesFromRecord(record));
    baselineRef.current = buildFirmwarePayload(valuesFromRecord(record));
    setErrors({});
    setFormError("");
    setSubmitting(false);
    inFlight.current = false;
  }, [open, record]);

  const isDirty = JSON.stringify(buildFirmwarePayload(values)) !== JSON.stringify(baselineRef.current);
  const leaveGuard = useUnsavedChangesGuard(isDirty && !submitting);
  const requestClose = () => leaveGuard.requestLeave(onClose);

  if (!open) return null;

  function update(field, value) {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setFormError("");
  }

  async function submit(event) {
    event.preventDefault();
    if (inFlight.current) return;
    const payload = buildFirmwarePayload(values);
    const validationErrors = validateFirmwarePayload(payload);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setFormError("");
      return;
    }
    const requestPayload = changedPayload(record, payload);
    if (isEdit && Object.keys(requestPayload).length === 0) {
      setFormError("No Firmware changes were supplied.");
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    setErrors({});
    setFormError("");
    try {
      const response = isEdit
        ? await updateFirmware(record.id, requestPayload)
        : await createFirmware(requestPayload);
      baselineRef.current = buildFirmwarePayload({ ...payload, ...(response?.data || {}) });
      toast.success(response?.message || `Firmware record ${isEdit ? "updated" : "created"}.`);
      onSaved?.(response?.data);
    } catch (error) {
      const fieldErrors = apiFieldErrors(error);
      if (Object.keys(fieldErrors).length) setErrors(fieldErrors);
      setFormError(
        error?.response?.status === 409
          ? "A Firmware record with this Model and Firmware Version already exists."
          : formatApiError(error)
      );
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const input = (field, type = "text") => (
    <Input
      id={`firmware-${field}`}
      type={type}
      value={values[field]}
      onChange={(event) => update(field, event.target.value)}
      disabled={submitting}
      aria-invalid={Boolean(errors[field])}
      aria-describedby={errors[field] ? `firmware-${field}-error` : undefined}
      className="form-control"
      data-testid={`firmware-field-${field}`}
    />
  );

  return (
    <Modal
      title={`${isEdit ? "Edit" : "Add"} Firmware`}
      onClose={requestClose}
      busy={submitting}
      testid="firmware-form-dialog"
    >
      <form onSubmit={submit} noValidate className="space-y-5" data-testid="firmware-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id="model" label="Model" error={errors.model}>{input("model")}</FormField>
          <FormField id="firmware_version" label="Firmware Version" error={errors.firmware_version}>{input("firmware_version")}</FormField>
          <FormField id="firmware_type" label="Firmware Type" error={errors.firmware_type}>{input("firmware_type")}</FormField>
          <FormField id="android_version" label="Android Version" error={errors.android_version}>{input("android_version")}</FormField>
        </div>
        <FormField id="firmware_url" label="Firmware URL" error={errors.firmware_url}>{input("firmware_url", "url")}</FormField>
        <FormField id="apdb_url" label="APDB URL" error={errors.apdb_url} optional>{input("apdb_url", "url")}</FormField>
        <FormField id="status" label="Status" error={errors.status}>
          <select
            id="firmware-status"
            className="form-control field-editable w-full"
            value={values.status}
            onChange={(event) => update("status", event.target.value)}
            disabled={submitting}
            aria-invalid={Boolean(errors.status)}
            aria-describedby={errors.status ? "firmware-status-error" : undefined}
            data-testid="firmware-field-status"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FormField>
        {formError ? (
          <p className="text-sm text-destructive" role="alert" data-testid="firmware-form-error">
            {formError}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="cancel" onClick={requestClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="success" loading={submitting} loadingLabel="Saving…" data-testid="firmware-save">
            Save
          </Button>
        </div>
      </form>
      {leaveGuard.dialog}
    </Modal>
  );
}
